import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';
import { useTagTeammates } from './useTagTeammates';

interface Props {
  ticketId: string;
  initialNote?: string | null;
  open: boolean;
  onClose: () => void;
}

export function HandoffModal({ ticketId, initialNote, open, onClose }: Props) {
  const [note, setNote] = useState(initialNote ?? '');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const tag = useTagTeammates();

  React.useEffect(() => {
    if (open) {
      setNote(initialNote ?? '');
      setError(null);
    }
  }, [open, initialNote]);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await ticketsApi.handoff(ticketId, { handoff_note: note });
      if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
      return res;
    },
    onSuccess: () => {
      push({ kind: 'success', title: 'Handoff saved' });
      invalidateAfterTicketAction(queryClient, ticketId);
      tag.reset();
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed to save handoff');
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Handoff note"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending || !note.trim()}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving...' : 'Save handoff'}
          </button>
        </>
      }
    >
      <Field label="Handoff note" required>
        <textarea
          className="form-textarea"
          rows={5}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={3000}
        />
      </Field>
      {tag.render()}
      {error ? <div className="form-error">{error}</div> : null}
    </Modal>
  );
}
